import React from 'react';
import { Loader2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { FINISHED_RUN_STATUSES } from '../../api/controlTestRuns';

// How a control-testing run (test_runs row, mapped by mapRunForList) is shown in a table.
// Shared by each control's Test Runs tab and the org-wide Test Runs page.

export const FINISHED = FINISHED_RUN_STATUSES;

/** Runs submitted for review, sent back, or signed off: not rerun, and their row is not deleted. */
export const LOCKED_RUN_STATUSES = ['prepared', 'in_review', 'signed_off'];

export const isRunLocked = (status) => LOCKED_RUN_STATUSES.includes(String(status || '').toLowerCase());

/** A row (all attempts of a rerun chain) can be deleted only when none of its attempts is locked. */
export const canDeleteAttempts = (statuses) => !(statuses || []).some(isRunLocked);

export function periodLabel(run) {
  const periods = run?.selected_periods?.periods;
  if (periods?.length) {
    return periods.length === 1
      ? periods[0].label
      : `${periods[0].label} — ${periods[periods.length - 1].label}`;
  }
  if (run?.testing_period_start && run?.testing_period_end) {
    return `${String(run.testing_period_start).split('T')[0]} — ${String(run.testing_period_end).split('T')[0]}`;
  }
  return '—';
}

export const WORKFLOW_STATUS_LABELS = {
  draft:                  { label: 'Draft',               color: 'text-gray-500' },
  period_selected:        { label: 'Period Set',          color: 'text-blue-600' },
  population_configured:  { label: 'Population Ready',   color: 'text-blue-700' },
  completed:              { label: 'Completed',           color: 'text-green-700' },
  prepared:               { label: 'Submitted for Review', color: 'text-purple-600' },
  in_review:              { label: 'Needs Revision',      color: 'text-orange-600' },
  signed_off:             { label: 'Signed Off',          color: 'text-teal-700' },
  failed:                 { label: 'Failed',              color: 'text-red-600' },
};

export function StatusCell({ status, workflowStatus, running, onRunningClick, populationNotRequired = false }) {
  if (running) {
    return (
      <button
        type="button"
        onClick={onRunningClick}
        title="View test progress"
        className="inline-flex items-center gap-1 text-indigo-600 text-xs font-medium hover:text-indigo-800 hover:underline"
      >
        <Loader2 className="h-4 w-4 animate-spin" /> Running…
      </button>
    );
  }
  const s = (workflowStatus || status || '').toLowerCase();
  if (s === 'completed' || s === 'signed_off') {
    return <span className={`inline-flex items-center gap-1 text-xs font-medium ${WORKFLOW_STATUS_LABELS[s]?.color || 'text-green-700'}`}><CheckCircle2 className="h-4 w-4" /> {WORKFLOW_STATUS_LABELS[s]?.label || 'Completed'}</span>;
  }
  if (s === 'failed') {
    return <span className="inline-flex items-center gap-1 text-red-600 text-xs font-medium"><XCircle className="h-4 w-4" /> Failed</span>;
  }
  // Without a population the "configured" state means: ready for evidence files.
  if (s === 'population_configured' && populationNotRequired) {
    return <span className="text-xs font-medium text-blue-700">Ready for Evidence</span>;
  }
  const info = WORKFLOW_STATUS_LABELS[s];
  if (info) return <span className={`text-xs font-medium ${info.color}`}>{info.label}</span>;
  return <span className="text-xs font-medium text-amber-600">Ready to Configure</span>;
}

export function ResultsCell({ run }) {
  const finished = FINISHED.includes((run.status || '').toLowerCase());
  if (!finished) return <span className="text-gray-300 text-xs">—</span>;
  const passed   = run.steps_passed  || 0;
  const failed   = run.steps_failed  || 0;
  const warnings = run.steps_warning || 0;
  const exceptions = run.total_exceptions || 0;
  const verdict = failed > 0 || exceptions > 0
    ? 'fail'
    : warnings > 0
      ? 'warn'
      : 'pass';
  return (
    <div className="flex items-center gap-2 text-xs">
      {verdict === 'pass' && <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" title="All passed" />}
      {verdict === 'warn' && <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" title="Warnings" />}
      {verdict === 'fail' && <XCircle       className="h-4 w-4 text-red-500 shrink-0"   title="Failures" />}
      <span className="text-green-600">{passed}✓</span>
      {failed   > 0 && <span className="text-red-500">{failed}✗</span>}
      {warnings > 0 && <span className="text-amber-500">{warnings}△</span>}
      {exceptions > 0 && <span className="text-amber-600">{exceptions} exc.</span>}
    </div>
  );
}
